import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  formatSerpAmount,
  getSerpInspectionRows,
  getSerpSettlementRows,
  isStoreClosureSettlement,
  type SerpStockAdjustmentSource,
} from '@/utils/approval/serpStockAdjustment';
import { SerpStockAdjustmentRoot } from './styles';

const TITLE_BG = '#fff2cc';
const LABEL_BG = '#f3f3f3';

const bg = (color: string) => ({ backgroundColor: color });

type TablesProps = {
  source: SerpStockAdjustmentSource;
  t: TFunction;
};

/** 레거시 #APV_SERP_INVENTORY_COUNT .form-group 의 표 3개(칸 구성·색 동일) */
const SerpStockAdjustmentTables = ({ source, t }: TablesProps) => {
  const inspections = getSerpInspectionRows(source);
  const settlements = getSerpSettlementRows(source);
  const isClosure = isStoreClosureSettlement(source);

  return (
    <>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={3}>
              {formatSerpText(source.BRAND_NM)}
            </th>
            <th style={bg(TITLE_BG)} colSpan={3}>
              {formatSerpText(source.SHOP_NM)}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} rowSpan={2}>
              {t('정산 대상자')}
            </th>
            <th style={bg(LABEL_BG)}>{t('코드')}</th>
            <th style={bg(LABEL_BG)}>{t('이름')}</th>
            <th style={bg(LABEL_BG)}>{t('근무시작일')}</th>
            <th style={bg(LABEL_BG)}>{t('사업자번호')}</th>
            <th style={bg(LABEL_BG)}>{t('비고')}</th>
          </tr>
          <tr>
            <td>{formatSerpText(source.SM_SHOP_CD)}</td>
            <td>{formatSerpText(source.SM_SHOP_NM)}</td>
            <td>{formatSerpText(source.SM_SHOP_STS_DE)}</td>
            <td>{formatSerpText(source.SM_SHOP_BRN)}</td>
            <td>{formatSerpText(source.SM_SHOP_RMK)}</td>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('재고조사 유형')}</th>
            <td colSpan={5}>{formatSerpText(source.STK_INVES_TYP_NM)}</td>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('재고조사 시행 일자')}</th>
            <td colSpan={5}>{formatSerpText(source.STCKT_DE)}</td>
          </tr>
        </tbody>
      </table>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={8}>
              {t('매장실사내용')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} rowSpan={2}>
              {t('시즌')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('전산')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('실사')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('차이')}
            </th>
            <th style={bg(LABEL_BG)} rowSpan={2}>
              {t('Loss 금액')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
          </tr>
          {inspections.map((row, index) => (
            <tr key={`inspection-${index}`}>
              <td style={bg(LABEL_BG)}>{formatSerpText(row.SSN_CD)}</td>
              <td>{formatSerpText(row.STD_STK_QTY)}</td>
              <td>{formatSerpAmount(row.SALE_AMT)}</td>
              <td>{formatSerpText(row.STCKT_QTY)}</td>
              <td>{formatSerpAmount(row.STCKT_AMT)}</td>
              <td>{formatSerpText(row.GAP_QTY)}</td>
              <td>{formatSerpAmount(row.GAP_AMT)}</td>
              <td>{formatSerpAmount(row.LOSS_AMT)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={8}>
              {t('정산내역')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('구분')}
            </th>
            <th style={bg(LABEL_BG)}>{t('Loss 금액')}</th>
            <th style={bg(LABEL_BG)}>{t('공급가율(%)')}</th>
            <th style={bg(LABEL_BG)}>{t('공제비율(%)')}</th>
            <th style={bg(LABEL_BG)}>{t('정산 금액')}</th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('비고')}
            </th>
          </tr>
          {settlements.map((row, index) => (
            <tr key={`settlement-${index}`}>
              {index === 0 && (
                <th style={bg(LABEL_BG)} rowSpan={settlements.length}>
                  Loss
                </th>
              )}
              <td style={bg(LABEL_BG)}>{formatSerpText(row.SSN_CD)}</td>
              <td>{formatSerpAmount(row.LOSS_AMT)}</td>
              <td>{formatSerpText(row.GI_RAT)}</td>
              <td>{formatSerpText(row.DED_RTO)}</td>
              <td>{formatSerpAmount(row.STMT_AMT)}</td>
              <td colSpan={2}>{formatSerpText(row.APRV_RMK)}</td>
            </tr>
          ))}
          {!isClosure && (
            <tr>
              <th style={bg(LABEL_BG)} colSpan={2}>
                {t('미처리금액')}
              </th>
              <td>{formatSerpAmount(source.EXC_AMT)}</td>
              <td />
              <td />
              <td />
              <td colSpan={2} />
            </tr>
          )}
          <tr>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('합계')}
            </th>
            <td>{formatSerpAmount(source.SUM_LOSS_AMT)}</td>
            <td />
            <td />
            <td>{formatSerpAmount(source.SUM_STMT_AMT)}</td>
            <td colSpan={2} />
          </tr>
          {isClosure && (
            <tr>
              <th style={bg(LABEL_BG)} colSpan={2}>
                {t('거래보증금')}
              </th>
              <td />
              <td />
              <td />
              <td>{formatSerpAmount(source.SM_TRD_GRT_AMT)}</td>
              <td colSpan={2} />
            </tr>
          )}
          <tr>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('보증보험금')}
            </th>
            <td />
            <td />
            <td />
            <td>{formatSerpAmount(source.SM_SURETY_BOND_AMT)}</td>
            <td colSpan={2} />
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('처리방안')}
            </th>
            <td colSpan={6}>{formatSerpText(source.RMK)}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

/** 작성 화면 표 — 저장 content 와 같은 마크업 */
export const SerpStockAdjustmentTablesView = (props: TablesProps) => (
  <SerpStockAdjustmentRoot>
    <div className="form-group">
      <SerpStockAdjustmentTables {...props} />
    </div>
  </SerpStockAdjustmentRoot>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildSerpStockAdjustmentContentHtml = (
  source: SerpStockAdjustmentSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpStockAdjustmentTables source={source} t={t} />
    </div>,
  );

/** 저장 content(레거시 문서 포함) 그대로 */
export const SerpStockAdjustmentSnapshot = ({
  content,
}: {
  content: string;
}) => (
  <SerpStockAdjustmentRoot>
    <SerpContentSnapshot content={content} />
  </SerpStockAdjustmentRoot>
);
